import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Save, ChevronLeft, ChevronRight, ShoppingCart, Package, ListChecks,
  Camera, Sparkles, CheckCircle2, Circle, RotateCcw, X, ClipboardCheck, Pencil,
  BookmarkPlus, Copy,
} from "lucide-react";
import { DropZone } from "../../components/DropZone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IN = "mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm";
const LB = "text-[10px] uppercase tracking-[0.2em] text-[#757067]";
const uid = () => Math.random().toString(36).slice(2, 10);
const LEER = {
  titel: "", kunde: "", kunde_id: "", datum: "", uhrzeit: "", ort: "", personen: 0,
  notizen: "", menu: "", einkauf: [], mitnehmen: [], todos: [], gerichte: [], menuekarten: [],
};

const LISTEN = [
  ["einkauf", "Einkaufen", ShoppingCart, true],
  ["mitnehmen", "Mitnehmen", Package, false],
  ["todos", "To-dos", ListChecks, false],
];

export default function KarteiTab({ pwd, prefill, onPrefillUsed, onOpenTab }) {
  const headers = { "X-Admin-Password": pwd };
  const [liste, setListe] = useState([]);
  const [aktiv, setAktiv] = useState(null);
  const [rezepte, setRezepte] = useState([]);
  const [gewaehlt, setGewaehlt] = useState([]);
  const [busy, setBusy] = useState(false);
  const [vorOrt, setVorOrt] = useState(false);
  const [lightbox, setLightbox] = useState(-1);
  const [vorlagen, setVorlagen] = useState([]);
  const [menuekarten, setMenuekarten] = useState([]);
  const [quelle, setQuelle] = useState("");          // "kartei:<id>" | "vorlage:<id>"
  const [uebFelder, setUebFelder] = useState({ einkauf: false, mitnehmen: true, todos: true });

  const load = useCallback(async () => {
    try {
      const [k, r, v, m] = await Promise.all([
        axios.get(`${API}/admin/karteien`, { headers }),
        axios.get(`${API}/admin/kalkulation/rezepte`, { headers }),
        axios.get(`${API}/admin/kartei-vorlagen`, { headers }),
        axios.get(`${API}/admin/menuekarten`, { headers }),
      ]);
      setListe(k.data || []);
      setRezepte(r.data || []);
      setVorlagen(v.data || []);
      setMenuekarten(m.data || []);
    } catch { toast.error("Karteien konnten nicht geladen werden"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  const hinterlegteKarten = (aktiv?.menuekarten || [])
    .map((id) => menuekarten.find((m) => m.id === id))
    .filter(Boolean);

  const menuekartenAuswahl = menuekarten
    .filter((m) => !(aktiv?.menuekarten || []).includes(m.id))
    .sort((a, b) => {
      const treffer = (m) => (aktiv?.kunde_id && m.kunde_id === aktiv.kunde_id ? 0 : 1);
      return treffer(a) - treffer(b) || String(b.datum || "").localeCompare(String(a.datum || ""));
    });

  const menuekarteDazu = (id) =>
    setFeld({ menuekarten: [...(aktiv.menuekarten || []), id] });

  const menuekarteWeg = (id) =>
    setFeld({ menuekarten: (aktiv.menuekarten || []).filter((x) => x !== id) });

  const anlegen = useCallback(async (vorbelegung = {}) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/karteien`, {
        ...LEER,
        datum: new Date().toISOString().slice(0, 10),
        ...vorbelegung,
        titel: vorbelegung.titel || (vorbelegung.kunde ? `Einsatz ${vorbelegung.kunde}` : "Neue Veranstaltung"),
      }, { headers });
      await load();
      setAktiv(data);
      return data;
    } catch (err) { toast.error(err.response?.data?.detail || "Anlegen fehlgeschlagen"); }
    finally { setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, pwd]);

  // Aus der Kundenakte: neue Kartei mit Kundendaten oder bestehende öffnen
  useEffect(() => {
    if (!prefill) return;
    (async () => {
      if (prefill.karteiId) {
        try {
          const { data } = await axios.get(`${API}/admin/karteien/${prefill.karteiId}`, { headers });
          setAktiv(data);
        } catch { toast.error("Kartei konnte nicht geladen werden"); }
      } else if (prefill.neu) {
        await anlegen(prefill.neu);
        toast.success("Kartei angelegt — Kundendaten sind übernommen");
      }
      onPrefillUsed?.();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const uebernehmen = async () => {
    if (!quelle) { toast.error("Bitte eine Quelle auswählen"); return; }
    const felder = Object.entries(uebFelder).filter(([, v]) => v).map(([k]) => k);
    if (!felder.length) { toast.error("Bitte mindestens eine Liste ankreuzen"); return; }
    const [typ, id] = quelle.split(":");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/karteien/${aktiv.id}/uebernehmen`,
        { quelle: typ, quelle_id: id, felder }, { headers });
      setAktiv(data.kartei);
      await load();
      toast.success(data.uebernommen
        ? `${data.uebernommen} Einträge übernommen — alle offen`
        : "Nichts Neues zu übernehmen");
    } catch (err) { toast.error(err.response?.data?.detail || "Übernehmen fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const alsVorlage = async () => {
    const name = window.prompt("Name der Vorlage:", aktiv.titel || "Vorlage");
    if (!name) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/karteien/${aktiv.id}/als-vorlage`,
        { name, felder: ["einkauf", "mitnehmen", "todos"] }, { headers });
      await load();
      toast.success("Als Vorlage gespeichert");
    } catch (err) { toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const vorlageLoeschen = async (v) => {
    if (!window.confirm(`Vorlage „${v.name}" löschen?`)) return;
    await axios.delete(`${API}/admin/kartei-vorlagen/${v.id}`, { headers });
    await load();
  };

  const speichern = async (k = aktiv, still = false) => {
    if (!k) return;
    try {
      const { data } = await axios.put(`${API}/admin/karteien/${k.id}`, k, { headers });
      setAktiv(data);
      await load();
      if (!still) toast.success("Gespeichert");
    } catch (err) { toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };

  const loeschen = async (k) => {
    if (!window.confirm(`„${k.titel || k.kunde}" samt Listen und Bildern löschen?`)) return;
    await axios.delete(`${API}/admin/karteien/${k.id}`, { headers });
    setAktiv(null);
    await load();
  };

  const setFeld = (patch) => setAktiv((a) => ({ ...a, ...patch }));
  const setPunkt = (feld, id, patch) => setAktiv((a) => ({
    ...a, [feld]: (a[feld] || []).map((p) => (p.id === id ? { ...p, ...patch } : p)),
  }));
  const addPunkt = (feld) => setAktiv((a) => ({
    ...a, [feld]: [...(a[feld] || []), { id: uid(), text: "", menge: "", erledigt: false }],
  }));
  const delPunkt = (feld, id) => setAktiv((a) => ({
    ...a, [feld]: (a[feld] || []).filter((p) => p.id !== id),
  }));

  const toggle = async (feld, id, wert) => {
    const neu = { ...aktiv, [feld]: (aktiv[feld] || []).map((p) => (p.id === id ? { ...p, erledigt: wert } : p)) };
    setAktiv(neu);
    await speichern(neu, true);
  };

  const zuruecksetzen = async (feld) => {
    const neu = { ...aktiv, [feld]: (aktiv[feld] || []).map((p) => ({ ...p, erledigt: false })) };
    setAktiv(neu);
    await speichern(neu, true);
  };

  const einkaufAusRezepten = async () => {
    if (!gewaehlt.length) { toast.error("Bitte Gänge auswählen"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/karteien/${aktiv.id}/einkauf-aus-rezepten`,
        { rezept_ids: gewaehlt, personen: Number(aktiv.personen) || 0 }, { headers });
      setAktiv((a) => ({ ...a, einkauf: data.einkauf }));
      toast.success(`${data.hinzugefuegt} Zutaten für ${data.personen} Personen übernommen`);
    } catch (err) { toast.error(err.response?.data?.detail || "Übernahme fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const bildHoch = async (files) => {
    setBusy(true);
    let ok = 0;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("datei", f);
        try {
          await axios.post(`${API}/admin/karteien/${aktiv.id}/bild`, fd, {
            headers: { ...headers, "Content-Type": "multipart/form-data" },
            params: { name: f.name.replace(/\.[^.]+$/, "") }, timeout: 120000,
          });
          ok += 1;
        } catch (err) {
          toast.error(`${f.name}: ${err.response?.data?.detail || "Upload fehlgeschlagen"}`);
        }
      }
      const { data } = await axios.get(`${API}/admin/karteien/${aktiv.id}`, { headers });
      setAktiv(data);
      if (ok) toast.success(ok === 1 ? "Bild hinterlegt" : `${ok} Bilder hinterlegt`);
    } finally { setBusy(false); }
  };

  const bildLoeschen = async (bid) => {
    await axios.delete(`${API}/admin/karteien/${aktiv.id}/bild/${bid}`, { headers });
    const { data } = await axios.get(`${API}/admin/karteien/${aktiv.id}`, { headers });
    setAktiv(data);
  };

  useEffect(() => {
    if (lightbox < 0) return;
    const esc = (e) => { if (e.key === "Escape") setLightbox(-1); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [lightbox]);

  if (!aktiv) {
    return (
      <div className="space-y-6" data-testid="kartei-tab">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <p className="text-sm text-[#757067] max-w-xl">
            Für jede Veranstaltung eine Kartei: was eingekauft und mitgenommen werden muss,
            To-dos und Fotos der Gerichte. Beim Kunden einfach die Kartei aufrufen und abhaken.
          </p>
          <button onClick={() => anlegen()} disabled={busy} data-testid="kartei-neu"
            className="bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Neue Kartei
          </button>
        </div>
        {liste.length === 0 ? (
          <p className="text-sm text-[#757067]" data-testid="kartei-leer">Noch keine Kartei angelegt.</p>
        ) : (
          <div className="space-y-2">
            {liste.map((k) => {
              const offen = (f) => (k[f] || []).filter((p) => !p.erledigt).length;
              return (
                <button key={k.id} onClick={() => setAktiv(k)} data-testid={`kartei-open-${k.id}`}
                  className="w-full text-left border border-[#E3DECE] bg-white px-4 py-4 hover:border-[#BA6D52] transition-colors">
                  <p className="font-serif text-lg text-[#2C2A26]">{k.titel || k.kunde || "Veranstaltung"}</p>
                  <p className="text-xs text-[#757067] mt-1">
                    {k.datum ? k.datum.split("-").reverse().join(".") : "ohne Datum"}
                    {k.uhrzeit ? ` · ${k.uhrzeit}` : ""}{k.kunde ? ` · ${k.kunde}` : ""}
                    {k.ort ? ` · ${k.ort}` : ""}{k.personen ? ` · ${k.personen} Personen` : ""}
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#BA6D52] mt-2">
                    {offen("einkauf")} Einkauf · {offen("mitnehmen")} mitnehmen · {offen("todos")} To-dos
                    {(k.gerichte || []).length ? ` · ${k.gerichte.length} Bilder` : ""}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const gerichte = aktiv.gerichte || [];
  const bildUrl = (gid) => `${API}/admin/karteien/${aktiv.id}/bild/${gid}?pwd=${encodeURIComponent(pwd)}`;

  return (
    <div className="space-y-6" data-testid="kartei-detail">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={() => setAktiv(null)} data-testid="kartei-zurueck"
          className="text-[10px] uppercase tracking-[0.2em] text-[#757067] hover:text-[#BA6D52] inline-flex items-center gap-2">
          <ChevronLeft size={14} /> Alle Karteien
        </button>
        <div className="flex gap-2">
          <button onClick={() => setVorOrt((v) => !v)} data-testid="kartei-vorort-toggle"
            className={`px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2 border ${
              vorOrt ? "bg-[#2C2A26] text-white border-[#2C2A26]"
                : "border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1]"}`}>
            {vorOrt ? <><Pencil size={14} /> Bearbeiten</> : <><ClipboardCheck size={14} /> Vor-Ort-Modus</>}
          </button>
          {!vorOrt && (
            <>
              <button onClick={() => speichern()} data-testid="kartei-speichern"
                className="bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
                <Save size={14} /> Speichern
              </button>
              <button onClick={() => loeschen(aktiv)} data-testid="kartei-loeschen"
                className="border border-[#E3DECE] text-[#757067] hover:bg-red-50 hover:text-red-700 px-3 py-2.5">
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      {vorOrt && (
        <div className="border border-[#2C2A26] bg-[#2C2A26] text-white p-5" data-testid="kartei-vorort-kopf">
          <p className="font-serif text-2xl">{aktiv.titel || aktiv.kunde || "Veranstaltung"}</p>
          <p className="text-xs text-white/70 mt-2">
            {aktiv.datum ? aktiv.datum.split("-").reverse().join(".") : "ohne Datum"}
            {aktiv.uhrzeit ? ` · ${aktiv.uhrzeit}` : ""}{aktiv.kunde ? ` · ${aktiv.kunde}` : ""}
            {aktiv.personen ? ` · ${aktiv.personen} Personen` : ""}
          </p>
          {aktiv.ort && <p className="text-sm text-white/90 mt-2">{aktiv.ort}</p>}
          {aktiv.menu && <p className="text-sm text-white/80 mt-3 whitespace-pre-line">{aktiv.menu}</p>}
          {hinterlegteKarten.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="kartei-vorort-menuekarten">
              {hinterlegteKarten.map((m) => (
                <button key={m.id} onClick={() => onOpenTab?.("menuekarten", { menu: m })}
                  data-testid={`kartei-vorort-menuekarte-${m.id}`}
                  className="border border-white/40 hover:bg-white/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em]">
                  {m.name || m.anlass}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {!vorOrt && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label className={LB}>Titel</label>
          <input value={aktiv.titel || ""} data-testid="kartei-titel"
            onChange={(e) => setFeld({ titel: e.target.value })} className={IN} />
        </div>
        <div>
          <label className={LB}>Kunde</label>
          <input value={aktiv.kunde || ""} data-testid="kartei-kunde"
            onChange={(e) => setFeld({ kunde: e.target.value })} className={IN} />
        </div>
        <div>
          <label className={LB}>Datum</label>
          <input type="date" value={aktiv.datum || ""} data-testid="kartei-datum"
            onChange={(e) => setFeld({ datum: e.target.value })} className={IN} />
        </div>
        <div>
          <label className={LB}>Uhrzeit</label>
          <input type="time" value={aktiv.uhrzeit || ""} data-testid="kartei-uhrzeit"
            onChange={(e) => setFeld({ uhrzeit: e.target.value })} className={IN} />
        </div>
        <div>
          <label className={LB}>Personen</label>
          <input type="number" min="0" value={aktiv.personen || 0} data-testid="kartei-personen"
            onChange={(e) => setFeld({ personen: Number(e.target.value) })} className={IN} />
        </div>
        <div className="md:col-span-3">
          <label className={LB}>Ort / Adresse</label>
          <input value={aktiv.ort || ""} data-testid="kartei-ort"
            onChange={(e) => setFeld({ ort: e.target.value })} className={IN} />
        </div>
        <div className="md:col-span-3">
          <label className={LB}>Menü</label>
          <textarea rows="3" value={aktiv.menu || ""} data-testid="kartei-menu"
            onChange={(e) => setFeld({ menu: e.target.value })} className={IN} />
        </div>
      </div>
      )}

      {!vorOrt && (
      <div className="border border-[#E3DECE] bg-white p-4" data-testid="kartei-menuekarten">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Menükarten zur Veranstaltung</p>
          <select value="" data-testid="kartei-menuekarte-waehlen"
            onChange={(e) => e.target.value && menuekarteDazu(e.target.value)}
            className="border border-[#E3DECE] bg-white px-3 py-2 text-sm">
            <option value="">Menükarte hinterlegen …</option>
            {menuekartenAuswahl.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name || m.anlass}{m.kunde_name ? ` · ${m.kunde_name}` : ""}{m.datum ? ` · ${m.datum.split("-").reverse().join(".")}` : ""}
              </option>
            ))}
          </select>
        </div>

        {hinterlegteKarten.length === 0 ? (
          <p className="text-sm text-[#757067] mt-3" data-testid="kartei-menuekarten-leer">
            Noch keine Menükarte hinterlegt — so hast du sie beim Einsatz direkt zur Hand.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-[#F0ECE2]" data-testid="kartei-menuekarten-liste">
            {hinterlegteKarten.map((m) => (
              <li key={m.id} className="py-2.5 flex items-center justify-between gap-3">
                <span className="min-w-0">
                  <span className="block text-sm text-[#2C2A26] truncate">{m.name || m.anlass}</span>
                  <span className="block text-xs text-[#757067] truncate">
                    {m.anlass}{m.kunde_name ? ` · ${m.kunde_name}` : ""}
                    {m.datum ? ` · ${m.datum.split("-").reverse().join(".")}` : ""}
                    {m.gaenge?.length ? ` · ${m.gaenge.length} Gänge` : ""}
                  </span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <button onClick={() => onOpenTab?.("menuekarten", { menu: m })}
                    data-testid={`kartei-menuekarte-oeffnen-${m.id}`}
                    className="border border-[#E3DECE] hover:border-[#BA6D52] px-3 py-1.5 text-[10px] uppercase tracking-[0.15em]">
                    Öffnen
                  </button>
                  <button onClick={() => menuekarteWeg(m.id)} data-testid={`kartei-menuekarte-entfernen-${m.id}`}
                    className="text-[#A39C90] hover:text-[#BA6D52]" title="Entfernen">
                    <X size={15} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      )}

      {!vorOrt && (
      <div className="border border-[#E3DECE] bg-[#FAFAF7] p-4" data-testid="kartei-uebernehmen">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Von früher übernehmen</p>
          <button onClick={alsVorlage} disabled={busy} data-testid="kartei-als-vorlage"
            className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-white hover:text-[#BA6D52] px-3 py-2 inline-flex items-center gap-2">
            <BookmarkPlus size={13} /> Diese Kartei als Vorlage
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <label className={LB}>Quelle</label>
            <select value={quelle} onChange={(e) => setQuelle(e.target.value)}
              data-testid="kartei-uebernehmen-quelle" className={IN}>
              <option value="">— auswählen —</option>
              {vorlagen.length > 0 && (
                <optgroup label="Vorlagen">
                  {vorlagen.map((v) => (
                    <option key={v.id} value={`vorlage:${v.id}`}>{v.name}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label={aktiv.kunde ? `Karteien von ${aktiv.kunde}` : "Frühere Karteien"}>
                {liste.filter((k) => k.id !== aktiv.id
                  && (!aktiv.kunde_id || k.kunde_id === aktiv.kunde_id)).map((k) => (
                  <option key={k.id} value={`kartei:${k.id}`}>
                    {k.titel || k.kunde}{k.datum ? ` · ${k.datum.split("-").reverse().join(".")}` : ""}
                  </option>
                ))}
              </optgroup>
              {aktiv.kunde_id && (
                <optgroup label="Alle anderen Karteien">
                  {liste.filter((k) => k.id !== aktiv.id && k.kunde_id !== aktiv.kunde_id).map((k) => (
                    <option key={k.id} value={`kartei:${k.id}`}>
                      {k.titel || k.kunde}{k.datum ? ` · ${k.datum.split("-").reverse().join(".")}` : ""}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
          <div>
            <label className={LB}>Was soll mitkommen?</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {LISTEN.map(([feld, label]) => (
                <button key={feld} onClick={() => setUebFelder((f) => ({ ...f, [feld]: !f[feld] }))}
                  data-testid={`kartei-ueb-feld-${feld}`}
                  className={`text-[11px] px-3 py-2 border inline-flex items-center gap-2 ${uebFelder[feld]
                    ? "border-[#BA6D52] text-[#BA6D52] bg-white"
                    : "border-[#E3DECE] text-[#757067] bg-white hover:bg-[#FAF5F1]"}`}>
                  {uebFelder[feld] ? <CheckCircle2 size={13} /> : <Circle size={13} />} {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <button onClick={uebernehmen} disabled={busy || !quelle} data-testid="kartei-uebernehmen-btn"
          className="mt-4 bg-[#BA6D52] text-white hover:bg-[#A55C43] disabled:opacity-50 px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} Übernehmen
        </button>
        <p className="text-[11px] text-[#9A958C] mt-2">
          Einträge werden ergänzt, Dopplungen übersprungen und alle Haken auf offen gesetzt.
        </p>
        {vorlagen.length > 0 && (
          <div className="mt-4 pt-3 border-t border-[#E3DECE]" data-testid="kartei-vorlagen">
            <p className={LB}>Gespeicherte Vorlagen</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {vorlagen.map((v) => (
                <span key={v.id} data-testid={`kartei-vorlage-${v.id}`}
                  className="text-[11px] inline-flex items-center gap-2 border border-[#E3DECE] bg-white px-3 py-1.5 text-[#575249]">
                  {v.name}
                  <button onClick={() => vorlageLoeschen(v)} data-testid={`kartei-vorlage-del-${v.id}`}
                    className="text-[#9A958C] hover:text-red-700">
                    <Trash2 size={11} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      {!vorOrt && (
      <div className="border border-[#E3DECE] bg-[#FAFAF7] p-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-2">
          Einkaufsliste aus Rezepten
        </p>
        <p className="text-xs text-[#757067] mb-3">
          Gänge auswählen — die Mengen werden mit {aktiv.personen || 0} Personen multipliziert und
          in die Einkaufsliste geschrieben.
        </p>
        <div className="flex flex-wrap gap-2">
          {rezepte.map((r) => {
            const an = gewaehlt.includes(r.id);
            return (
              <button key={r.id} data-testid={`kartei-rezept-${r.id}`}
                onClick={() => setGewaehlt((g) => (an ? g.filter((x) => x !== r.id) : [...g, r.id]))}
                className={`text-[11px] px-3 py-2 border ${an
                  ? "border-[#BA6D52] text-[#BA6D52] bg-white"
                  : "border-[#E3DECE] text-[#757067] bg-white hover:bg-[#FAF5F1]"}`}>
                {r.name}
              </button>
            );
          })}
        </div>
        <button onClick={einkaufAusRezepten} disabled={busy} data-testid="kartei-einkauf-generieren"
          className="mt-4 border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-4 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Zutaten übernehmen
        </button>
      </div>
      )}

      {LISTEN.map(([feld, label, Icon, mitMenge]) => {
        const punkte = aktiv[feld] || [];
        const fertig = punkte.filter((p) => p.erledigt).length;
        const offen = punkte.length - fertig;
        if (vorOrt) {
          return (
            <div key={feld} className="border border-[#E3DECE] bg-white" data-testid={`kartei-liste-${feld}`}>
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#F0EBE0] flex-wrap">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#BA6D52] inline-flex items-center gap-2">
                  <Icon size={14} /> {label}
                </p>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[#757067]" data-testid={`kartei-${feld}-fortschritt`}>
                    {fertig} / {punkte.length} erledigt
                  </span>
                  {fertig > 0 && (
                    <button onClick={() => zuruecksetzen(feld)} data-testid={`kartei-${feld}-reset`}
                      className="text-[10px] uppercase tracking-[0.15em] text-[#757067] hover:text-[#BA6D52] inline-flex items-center gap-1.5">
                      <RotateCcw size={12} /> Zurücksetzen
                    </button>
                  )}
                </div>
              </div>
              <div className="h-1 bg-[#F0EBE0]">
                <div className="h-1 bg-[#BA6D52] transition-all"
                  style={{ width: punkte.length ? `${(fertig / punkte.length) * 100}%` : "0%" }} />
              </div>
              {punkte.length === 0 ? (
                <p className="text-sm text-[#9A958C] px-4 py-5">Nichts eingetragen.</p>
              ) : (
                <ul className="divide-y divide-[#F5F2EC]">
                  {punkte.map((p) => (
                    <li key={p.id}>
                      <button onClick={() => toggle(feld, p.id, !p.erledigt)}
                        data-testid={`kartei-${feld}-tap-${p.id}`}
                        className={`w-full text-left flex items-center gap-4 px-4 py-4 transition-colors ${
                          p.erledigt ? "bg-[#FAFAF7]" : "hover:bg-[#FAF5F1]"}`}>
                        {p.erledigt
                          ? <CheckCircle2 size={26} className="text-[#BA6D52] shrink-0" />
                          : <Circle size={26} className="text-[#D6D0C4] shrink-0" />}
                        <span className={`flex-1 text-base ${p.erledigt ? "line-through text-[#9A958C]" : "text-[#2C2A26]"}`}>
                          {p.text || "—"}
                        </span>
                        {mitMenge && p.menge && (
                          <span className={`text-sm tabular-nums ${p.erledigt ? "text-[#B5AFA3]" : "text-[#757067]"}`}>
                            {p.menge}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {offen === 0 && punkte.length > 0 && (
                <p className="text-xs text-[#BA6D52] px-4 py-3 border-t border-[#F0EBE0]"
                  data-testid={`kartei-${feld}-komplett`}>
                  Alles abgehakt — komplett.
                </p>
              )}
            </div>
          );
        }
        return (
        <div key={feld} className="border border-[#E3DECE] bg-white p-4" data-testid={`kartei-liste-${feld}`}>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-3 inline-flex items-center gap-2">
            <Icon size={13} /> {label} · {offen} offen
          </p>
          <div className="space-y-2">
            {(aktiv[feld] || []).map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                <input type="checkbox" checked={!!p.erledigt} data-testid={`kartei-${feld}-check-${p.id}`}
                  onChange={(e) => toggle(feld, p.id, e.target.checked)} className="w-5 h-5" />
                <input value={p.text} data-testid={`kartei-${feld}-text-${p.id}`}
                  onChange={(e) => setPunkt(feld, p.id, { text: e.target.value })}
                  className={`flex-1 bg-white border border-[#E3DECE] px-3 py-2 text-sm ${p.erledigt ? "line-through text-[#9A958C]" : ""}`} />
                {mitMenge && (
                  <input value={p.menge || ""} placeholder="Menge" data-testid={`kartei-${feld}-menge-${p.id}`}
                    onChange={(e) => setPunkt(feld, p.id, { menge: e.target.value })}
                    className="w-[110px] bg-white border border-[#E3DECE] px-3 py-2 text-sm" />
                )}
                <button onClick={() => delPunkt(feld, p.id)} data-testid={`kartei-${feld}-del-${p.id}`}
                  className="border border-[#E3DECE] text-[#757067] hover:text-red-700 p-2">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
          <button onClick={() => addPunkt(feld)} data-testid={`kartei-${feld}-add`}
            className="mt-3 text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-4 py-2 inline-flex items-center gap-2">
            <Plus size={13} /> Zeile
          </button>
        </div>
        );
      })}

      <div className="border border-[#E3DECE] bg-white p-4" data-testid="kartei-bilder">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-3 inline-flex items-center gap-2">
          <Camera size={13} /> Bilder der Gerichte
        </p>
        {!vorOrt && (
          <DropZone
            testid="kartei-bild-drop"
            hinweis="Fotos der Gerichte — mehrere Bilder gleichzeitig möglich"
            aktion="Bild hinzufügen"
            icon={Camera}
            variant="outline"
            accept="image/*"
            multiple
            busy={busy}
            onFiles={bildHoch}
          />
        )}
        {(aktiv.gerichte || []).length > 0 ? (
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 ${vorOrt ? "" : "mt-4"}`}>
            {aktiv.gerichte.map((g, i) => (
              <div key={g.id} className="border border-[#E3DECE]" data-testid={`kartei-bild-${g.id}`}>
                <button onClick={() => setLightbox(i)} data-testid={`kartei-bild-gross-${g.id}`}
                  className="block w-full group relative overflow-hidden"
                  aria-label={`${g.name} groß ansehen`}>
                  <img src={bildUrl(g.id)} alt={g.name}
                    className="w-full h-32 object-cover transition-transform duration-300 group-hover:scale-105" />
                  <span className="absolute inset-0 bg-[#2C2A26]/0 group-hover:bg-[#2C2A26]/20 transition-colors" />
                </button>
                <div className="p-2 flex items-center justify-between gap-2">
                  <span className="text-xs text-[#575249] truncate">{g.name}</span>
                  {!vorOrt && (
                    <button onClick={() => bildLoeschen(g.id)} data-testid={`kartei-bild-del-${g.id}`}
                      className="text-[#757067] hover:text-red-700">
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : vorOrt && (
          <p className="text-sm text-[#9A958C]">Keine Fotos hinterlegt.</p>
        )}
      </div>

      {!vorOrt && (
      <div>
        <label className={LB}>Notizen</label>
        <textarea rows="4" value={aktiv.notizen || ""} data-testid="kartei-notizen"
          onChange={(e) => setFeld({ notizen: e.target.value })} className={IN} />
      </div>
      )}

      {vorOrt && aktiv.notizen && (
        <div className="border border-[#E3DECE] bg-white p-4" data-testid="kartei-notizen-anzeige">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-2">Notizen</p>
          <p className="text-sm text-[#575249] whitespace-pre-line">{aktiv.notizen}</p>
        </div>
      )}

      {!vorOrt && (
      <button onClick={() => speichern()} data-testid="kartei-speichern-unten"
        className="w-full bg-[#2C2A26] text-white hover:bg-[#1F1D1A] px-5 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2">
        <Save size={15} /> Kartei speichern
      </button>
      )}

      {lightbox >= 0 && gerichte[lightbox] && (
        <div className="fixed inset-0 z-[80] bg-[#15130F]/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(-1)} data-testid="kartei-lightbox">
          <button onClick={() => setLightbox(-1)} data-testid="kartei-lightbox-close"
            className="absolute top-5 right-5 text-white/70 hover:text-white p-2">
            <X size={26} />
          </button>
          {gerichte.length > 1 && (
            <>
              <button data-testid="kartei-lightbox-prev"
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + gerichte.length) % gerichte.length); }}
                className="absolute left-3 md:left-8 text-white/60 hover:text-white p-3">
                <ChevronLeft size={34} />
              </button>
              <button data-testid="kartei-lightbox-next"
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % gerichte.length); }}
                className="absolute right-3 md:right-8 text-white/60 hover:text-white p-3">
                <ChevronRight size={34} />
              </button>
            </>
          )}
          <figure className="max-w-5xl max-h-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}>
            <img src={bildUrl(gerichte[lightbox].id)} alt={gerichte[lightbox].name}
              data-testid="kartei-lightbox-img"
              className="max-h-[78vh] w-auto object-contain shadow-2xl" />
            <figcaption className="text-white/80 text-sm mt-4 text-center">
              {gerichte[lightbox].name}
              {gerichte.length > 1 && (
                <span className="text-white/40"> · {lightbox + 1} / {gerichte.length}</span>
              )}
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
